import { type Component, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import type { CompanyV2 } from "@/domains/company/company/api/types";
import { CompanyMonogram } from "@/domains/company/company/components/CompanyMonogram";
import { headquartersCity } from "@/domains/company/company/utils/locations";
import { usePostHog } from "@/hooks/usePostHog";
import {
  AboutSection,
  BenefitsSection,
  DetailsSection,
} from "./sections-about";
import { LocationsSection, SocialSection } from "./sections-links";

/**
 * Il foglio «Informazioni azienda»: blocco identità agganciato in cima e, sotto,
 * le cinque sezioni nell'ordine di AC 4.
 *
 * Qui si **compone soltanto**. Ogni sezione porta con sé il proprio gate — senza
 * dati non renderizza nulla — e il proprio `defaultOpen`, quindi questo file non
 * ha un solo `<Show>` attorno alle sezioni e non passa un solo `defaultOpen`:
 * ripetere qui una condizione che vive già accanto alle righe che decide
 * significa creare due verità che prima o poi divergono. La conseguenza è che
 * una company con il solo nome — l'esito più frequente della creazione manuale,
 * non un caso limite — produce identità e nient'altro, senza intestazioni orfane
 * né divisori doppi (EC1), senza che questo file debba saperlo.
 *
 * Le stringhe di questo file riusano chiavi che esistono già
 * (`joblist.detailSheet.viewCompany`, `company.publicPage.identity.verified`):
 * il blocco identità non ne introduce nessuna di nuova.
 */
export const CompanyInfoSheet: Component<{ company: CompanyV2 }> = (props) => {
  const { t } = useI18n();
  const { capture } = usePostHog();

  /**
   * «settore · sede», con le parti assenti tolte prima di unire: se mancano
   * entrambe la riga non esiste affatto, invece di lasciare sotto il nome un
   * paragrafo vuoto alto quanto una riga di testo.
   *
   * Nessuno dei due valori è la colonna grezza, e per due ragioni diverse.
   *
   * Il settore si legge `.trim()`ato perché qui le due parti si **uniscono**:
   * `industry` di soli spazi — che le Impostazioni salvano davvero, il loro
   * controllo di campo vuoto considera vuota `""` ma non `"   "` — passerebbe
   * il `filter(Boolean)` e stamperebbe `"    · Milano"`, un separatore attaccato
   * al nulla. È il caso che la vetrina non può produrre, dove `industry` è un
   * paragrafo a sé.
   *
   * La sede passa da `headquartersCity`, la stessa utility della identity card
   * pubblica, perché `headquarters_location` **non è una città** su ogni riga:
   * l'import LinkedIn ci scrive la riga intera. Stampandola grezza la stessa
   * sede compariva in tre forme — «Milano, Lombardia, Italia» qui, «Milano ·
   * Lombardia, Italia» nella sezione Sedi due righe sotto, «Milano» sulla
   * vetrina — e cadeva anche il razionale di AC 10, che toglie la riga «Sede
   * principale» da Dettagli *perché* la sede sta già qui: vero solo se qui è la
   * stessa cosa.
   */
  const secondaryLine = () =>
    [props.company.industry?.trim(), headquartersCity(props.company)]
      .filter(Boolean)
      .join(" · ");

  return (
    <div class="w-full">
      {/*
        Identità agganciata in cima: resta ferma mentre il contenuto le scorre
        sotto. Gli inset di uno sticky sono relativi al **padding-box** dello
        scroller, quindi per agganciarsi al bordo vero serve un top negativo pari
        al padding del contenitore — qui `ScrollableContent`, che è `p-2 lg:p-6`.
        L'invariante da tenere a ogni breakpoint è `-top-N == -mt-N == padding`:
        finché regge, la chiusura di una sezione non può far saltare il blocco.
        (Il prototipo usa `-top-4 md:` perché la sua harness aveva un padding
        diverso: copiarlo disallineerebbe di 8px su mobile e sbaglierebbe il
        breakpoint.)

        `bg-background` e non `bg-card`: è il fondo che `fixedSheetVariants` dà
        alla sheet, e il blocco deve sparire dentro il suo contenitore invece di
        disegnarci sopra una scheda.

        Niente ombra, niente transizione su bordo o ombra, nessuno scroll
        listener: il `border-b` è **statico**. Con poche sezioni non c'è scroll
        affatto, e uno sticky che si «accende» simulerebbe uno scroll inesistente.
      */}
      <div class="-top-2 -mx-2 -mt-2 lg:-top-6 lg:-mx-6 lg:-mt-6 sticky z-10 border-b bg-background px-2 pt-4 pb-3 lg:px-6 lg:pt-6 lg:pb-4">
        <div class="flex items-center gap-3">
          {/* Il monogramma copre da solo il logo morto: iniziali, mai il glifo
              di immagine rotta del browser. */}
          <CompanyMonogram
            name={props.company.name}
            logoUrl={props.company.logo_url}
            class="size-11 rounded-md text-base"
          />
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <p class="font-medium">{props.company.name}</p>
              {/*
                Il segno di verifica è un fatto, non una decorazione: l'icona è
                nascosta agli assistivi e la parola la porta il testo sr, con la
                stessa chiave della pagina pubblica — un `title` hardcoded non è
                né tradotto né annunciato in modo affidabile. Falso o assente:
                non compare nulla al suo posto.
              */}
              <Show when={props.company.is_verified === true}>
                <span class="inline-flex items-center">
                  <i
                    class="ph-fill ph-seal-check text-base text-green-600"
                    aria-hidden="true"
                  />
                  <span class="sr-only">
                    {t("company.publicPage.identity.verified")}
                  </span>
                </span>
              </Show>
            </div>
            <Show when={secondaryLine()}>
              {(line) => (
                <p class="truncate text-muted-foreground text-sm">{line()}</p>
              )}
            </Show>
          </div>
          {/*
            Un `<a>` vero verso una scheda nuova, non una navigazione interna:
            la sheet vive sopra un'offerta selezionata (e, in preview, dentro un
            flusso di creazione ancora aperto) che il ritorno indietro non
            saprebbe ricostruire. `rel` completo perché `target="_blank"` da solo
            lascerebbe alla pagina aperta un riferimento a questa.
          */}
          <a
            href={`/company/${props.company.id}`}
            target="_blank"
            rel="noopener noreferrer"
            class="shrink-0 text-primary text-sm hover:underline"
            onClick={() =>
              capture("click_company_link", {
                company_id: props.company.id,
              })
            }
          >
            {t("joblist.detailSheet.viewCompany")} →
          </a>
        </div>
      </div>

      {/*
        Il gruppo esiste per una riga sola: spegnere il `border-b` dell'ultima
        sezione **renderizzata**. Ogni sezione porta il proprio divisore inferiore
        e l'ultima lo lascerebbe appeso sopra il padding del contenitore, una riga
        che non separa niente. `*:last-child` guarda gli elementi, quindi segue le
        sezioni che spariscono: con la sola «Chi siamo» è lei a perdere il bordo,
        con zero sezioni non c'è nulla da spegnere.

        Nient'altro: niente `<Show>` esterni (i gate stanno dentro le sezioni),
        niente `defaultOpen` passato da qui (AC 5 vive dentro le prime due),
        nessun wrapper per sezione. E niente `space-y`/`divide-*`, che sopra dei
        `border-b` propri produrrebbero i divisori doppi vietati da EC1.
      */}
      <div class="[&>*:last-child]:border-b-0">
        <AboutSection company={props.company} />
        <DetailsSection company={props.company} />
        <BenefitsSection company={props.company} />
        <LocationsSection company={props.company} />
        <SocialSection company={props.company} />
      </div>
    </div>
  );
};
